import { ListaEventoRequest } from 'api/Assessor/evento';
import {
  Fragment,
  useContext,
  useEffect,
  useLayoutEffect,
  useMemo,
  useReducer,
  useState,
} from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { IReducer } from 'store/Reducer';

import { getOptionsFromParams } from 'helpers/utils/dropdownUtils';
import ErrorHandler from 'helpers/utils/ErrorHandler';
import { formatData } from 'helpers/utils/HTMLUtils';

import {
  AddIcon,
  Button,
  ButtonThemes,
  Col,
  DatePicker,
  Input,
  RefreshIcon,
  Row,
  SearchIcon,
  Select as SelectTest,
} from 'plataforma-fundacao-componentes';
import { assessorActionListarCooperativasUsuario } from 'store/modules/assessor/actions';

import { AuthContext } from 'context/authContext/AuthContext';
import {
  AssemblyEventText,
  AssemblyModalities,
  AssemblyModalityText,
  AssemblyType,
  AssemblyTypeText,
  SituationText,
} from 'helpers/enums/Assembleia';
import { getProfileByJWTRoles } from 'helpers/utils/AuthUtils';
import {
  getParsedJWTAssessorAccessToken,
  JWTAssessorJson,
} from 'helpers/utils/JWTUtils';
import {
  AssessorCadastroEventoRoutePath,
  AssessorCadastroReuniaoRoutePath,
} from 'library/Routes';
import translation from 'locales/translation';
import moment from 'moment';
import { useHistory } from 'react-router-dom';
import { getAllEventStatus } from 'services/assessor';
import {
  AuthUserPerfilId,
  IAuthUserPerfil,
} from 'store/modules/auth/IAuthUserPerfil';
import { FiltroValidator } from './FiltroValidator';
import {
  filtrosInitialState,
  filtrosReducer,
  initialEndDate,
  initialStartDate,
} from './reducer';

interface Props {
  onChangeFilter: (filters: Partial<ListaEventoRequest> | undefined) => void;
  onValidateDate: (hasError: boolean) => void;
}

const homePrefix = translation.Assessor.TelaInicial;

export default function Filtros({ onChangeFilter }: Props) {
  const [sent, setSent] = useState<boolean>(false);
  const [defaultValue, setDefaultValue] = useState<boolean>(false);
  const [loader, setLoader] = useState<boolean>(true);

  const dispatch = useDispatch();
  const history = useHistory();

  const [state, action] = useReducer(filtrosReducer, filtrosInitialState);

  const { user } = useContext(AuthContext);
  const parsedToken: JWTAssessorJson | null = getParsedJWTAssessorAccessToken(
    user!.access_token
  );

  const { cooperativas, cooperativasIsLoading, listaEventoIsLoading } =
    useSelector((state: IReducer) => state.assessor);

  const perfil: IAuthUserPerfil | null = parsedToken
    ? getProfileByJWTRoles(parsedToken.roles)
    : null;

  let filtroStorage: any = sessionStorage.getItem('filter-assessor');
  let filtroObj: any = JSON.parse(filtroStorage);

  const eventTypeOptions = useMemo(() => {
    return [
      { label: AssemblyTypeText.AGN, value: AssemblyType.AGN },
      { label: AssemblyTypeText.AGE, value: AssemblyType.AGE },
      { label: AssemblyTypeText.AGO, value: AssemblyType.AGO },
      { label: AssemblyTypeText.AOE, value: AssemblyType.AOE },
      { label: AssemblyTypeText.REU, value: AssemblyType.REU },
    ];
  }, []);

  const modalidadesOptions = useMemo(() => {
    return [
      {
        label: AssemblyModalityText.OFFLINE,
        value: AssemblyModalities.OFFLINE,
      },
      {
        label: AssemblyModalityText.ONLINE,
        value: AssemblyModalities.ONLINE,
      },
      {
        label: AssemblyModalityText.HIBRIDO_PRESENCIAL_DIGITAL,
        value: AssemblyModalities.HIBRIDO_PRESENCIAL_DIGITAL,
      },
      {
        label: AssemblyModalityText.HIBRIDO_DIGITAL_AGENCIA,
        value: AssemblyModalities.HIBRIDO_DIGITAL_AGENCIA,
      },
      {
        label: AssemblyModalityText.HIBRIDO_SIMULTANEO,
        value: AssemblyModalities.HIBRIDO_SIMULTANEO,
      },
    ];
  }, []);

  const testOptions = useMemo(() => {
    return [
      {
        label: AssemblyEventText.ALL,
        value: null,
      },
      {
        label: AssemblyEventText.ONLY_TEST,
        value: true,
      },
      {
        label: AssemblyEventText.ONLY_OFFICIALS,
        value: false,
      },
    ];
  }, []);

  useEffect(() => {
    if (filtroStorage !== null) {
      action({
        type: 'setFilter',
        value: {
          idEvento: filtroObj!?.idEvento!?.replace(/\D/g, ''),
          startDate: filtroObj!?.dataInicial.substring(
            0,
            filtroObj!?.dataInicial.indexOf('T')
          ),
          endDate: filtroObj!?.dataFinal.substring(
            0,
            filtroObj!?.dataFinal.indexOf('T')
          ),
          flgEventoTeste: filtroObj!?.flgEventoTeste,
        },
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sent]);

  const filters: Partial<ListaEventoRequest> | undefined = useMemo(() => {
    return {
      idEvento: state.filter.idEvento,
      tiposEventos: state.filter.tiposEventos,
      modalidades: state.filter.modalidades,
      status: state.filter.status,
      flgEventoTeste: state.filter.flgEventoTeste,
      cooperativas:
        cooperativas!?.length === 1
          ? Array(state.filter.cooperativas)
          : state.filter.cooperativas,
      dataInicial: `${moment(state.filter.startDate, 'YYYY-MM-DD').format(
        'YYYY-MM-DD'
      )}T00:00:00`,
      dataFinal: `${moment(state.filter.endDate, 'YYYY-MM-DD').format(
        'YYYY-MM-DD'
      )}T23:59:59`,
    } as Partial<ListaEventoRequest>;
  }, [
    cooperativas,
    state.filter.idEvento,
    state.filter.tiposEventos,
    state.filter.modalidades,
    state.filter.flgEventoTeste,
    state.filter.status,
    state.filter.cooperativas,
    state.filter.startDate,
    state.filter.endDate,
  ]);

  const validator = useMemo(() => {
    return FiltroValidator({
      idEvent: filters?.idEvento!,
      coop: filters?.cooperativas!,
      eventType: filters?.tiposEventos!,
      modalidade: filters?.modalidades!,
      status: filters?.status!,
      startDate: filters?.dataInicial!,
      endDate: filters?.dataFinal!,
    });
  }, [
    filters?.idEvento,
    filters?.cooperativas,
    filters?.tiposEventos,
    filters?.modalidades,
    filters?.status,
    filters?.dataInicial,
    filters?.dataFinal,
  ]);

  useEffect(() => {
    if (cooperativas !== null || cooperativasIsLoading) return;
    dispatch(assessorActionListarCooperativasUsuario(undefined, ErrorHandler));

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useLayoutEffect(() => {
    setLoader(true);

    if (cooperativas!?.length > 1 && state.situation.length) {
      action({
        type: 'setFilter',
        value: {
          cooperativas:
            filtroStorage !== null
              ? filtroObj.cooperativas
              : cooperativas!?.map(
                  (x: { codCooperativa: string }) => x.codCooperativa
                ),
          tiposEventos:
            filtroStorage !== null
              ? filtroObj.tiposEventos
              : eventTypeOptions.map((x: { value: string }) => x.value),
          modalidades:
            filtroStorage !== null
              ? filtroObj.modalidades
              : modalidadesOptions.map((x: { value: string }) => x.value),
          status:
            filtroStorage !== null
              ? filtroObj.status
              : state.situation.map((x) => x.value),
        },
      });
      setLoader(false);

      setTimeout(() => {
        if (!loader && state.filter.cooperativas?.length) {
          onChangeFilter(filters);
          setSent(false);
        }
      }, 100);
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cooperativasIsLoading, cooperativas, loader, state.situation]);

  useEffect(() => {
    if (!cooperativasIsLoading && cooperativas?.length === 1) {
      action({
        type: 'setFilter',
        value: {
          tiposEventos:
            filtroStorage !== null
              ? filtroObj.tiposEventos
              : eventTypeOptions.map((x: { value: string }) => x.value),
          modalidades:
            filtroStorage !== null
              ? filtroObj.modalidades
              : modalidadesOptions.map((x: { value: string }) => x.value),
          status:
            filtroStorage !== null
              ? filtroObj.status
              : state.situation.map((x) => x.value),
        },
      });
      setLoader(false);

      setTimeout(() => {
        if (!loader && state.filter.cooperativas?.length) {
          onChangeFilter({ ...filters });
          setSent(false);
        }
      }, 100);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state.filter.cooperativas, cooperativasIsLoading, state.situation]);

  const disabledCleanButton = useMemo(() => {
    if (
      state.filter.idEvento ||
      !state.filter.tiposEventos ||
      state.filter.flgEventoTeste !== null ||
      !state.filter.tiposEventos.length ||
      eventTypeOptions.length !== state.filter.tiposEventos.length ||
      !state.filter.modalidades ||
      !state.filter.modalidades.length ||
      modalidadesOptions.length !== state.filter.modalidades.length ||
      (cooperativas!?.length > 1
        ? cooperativas?.length !== state.filter.cooperativas?.length
        : !state.filter.cooperativas) ||
      !state.filter.status ||
      !state.filter.status.length ||
      state.situation.length !== state.filter.status.length ||
      state.filter.startDate !== initialStartDate ||
      state.filter.endDate !== initialEndDate
    ) {
      return false;
    }

    return true;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state.filter]);

  useEffect(() => {
    getAllEventStatus().then((resp: any) => {
      action({
        type: 'setSituation',
        value: resp.data.map((s: any) => {
          return {
            label: SituationText[s.name as keyof typeof SituationText],
            value: s.name,
          };
        }),
      });
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (defaultValue) {
      onChangeFilter(filters);

      setTimeout(() => {
        setDefaultValue(false);
      }, 100);
    }
  }, [defaultValue, filters, onChangeFilter]);

  console.log(filters);

  return (
    <Fragment>
      <Row>
        <Col cols={[12, 6, 4, 4, 4]}>
          <Input
            data-testid={'filtro-por-codigo-evento'}
            value={state.filter.idEvento}
            onChange={(e) => {
              action({
                type: 'setFilter',
                value: {
                  idEvento: e.target.value.replace(/\D/g, ''),
                  tiposEventos: eventTypeOptions.map(
                    (x: { value: string }) => x.value
                  ),
                  modalidades: modalidadesOptions.map(
                    (x: { value: string }) => x.value
                  ),
                  status: state.situation.map((x) => x.value),
                  cooperativas:
                    cooperativas!?.length === 1
                      ? cooperativas![0].codCooperativa
                      : cooperativas?.map((coops) => coops.codCooperativa),
                  startDate: initialStartDate,
                  endDate: initialEndDate,
                },
              });
            }}
            label={homePrefix.filter.codEvento}
            placeholder={homePrefix.filter.codEventoPlaceholder}
            inputMode={'numeric'}
            maxLength={5}
            loading={cooperativasIsLoading}
            disabled={cooperativasIsLoading}
          />
        </Col>
        <Col cols={[12, 6, 4, 4, 4]}>
          <SelectTest
            data-testid={'filtro-por-tipo-evento'}
            label={homePrefix.filter.tipoEvento}
            placeholder={homePrefix.filter.tipoEventoPlaceholder}
            options={eventTypeOptions}
            value={state.filter.tiposEventos}
            onChange={(value) => {
              action({
                type: 'setFilter',
                value: {
                  tiposEventos: value,
                },
              });
            }}
            panelFilter
            multiple
            loading={cooperativasIsLoading}
            disabled={
              (String(state.filter.idEvento) !== '' &&
                state.filter.idEvento !== undefined) ||
              cooperativasIsLoading
            }
            error={sent && !!validator && validator.eventTypeIsRequired().error}
            helperText={
              sent && !!validator && validator.eventTypeIsRequired().error
                ? validator.eventTypeIsRequired().helperText
                : ''
            }
            clearButton={
              String(state.filter.idEvento) === '' ||
              state.filter.idEvento === undefined
            }
          />
        </Col>
        <Col cols={[12, 12, 4, 4, 4]}>
          <SelectTest
            data-testid={'filtro-por-modalidade'}
            label={homePrefix.filter.modalidadeEvento}
            placeholder={homePrefix.filter.modalidadeEventoPlaceholder}
            options={modalidadesOptions}
            value={state.filter.modalidades}
            onChange={(value) => {
              action({
                type: 'setFilter',
                value: {
                  modalidades: value,
                },
              });
            }}
            panelFilter
            multiple
            loading={cooperativasIsLoading}
            disabled={
              (String(state.filter.idEvento) !== '' &&
                state.filter.idEvento !== undefined) ||
              cooperativasIsLoading
            }
            error={
              sent && !!validator && validator.modalidadeIsRequired().error
            }
            helperText={
              sent && !!validator && validator.modalidadeIsRequired().error
                ? validator.modalidadeIsRequired().helperText
                : ''
            }
            clearButton={
              String(state.filter.idEvento) === '' ||
              state.filter.idEvento === undefined
            }
          />
        </Col>
        <Col cols={[12, 6, 6, 3, 3]}>
          <SelectTest
            data-testid={'filtro-por-coop'}
            label={homePrefix.filter.coopEvento}
            placeholder={homePrefix.filter.coopEventoPlaceholder}
            options={getOptionsFromParams(
              cooperativas!?.filter(
                (c: { codCooperativa: string; nomeCooperativa: string }) => c
              ),
              'codCooperativa',
              (coop) => `${coop.codCooperativa} - ${coop.nomeCooperativa}`
            )}
            value={state.filter.cooperativas}
            onChange={(value) => {
              action({
                type: 'setFilter',
                value: {
                  cooperativas: value,
                },
              });
            }}
            disabled={cooperativasIsLoading || cooperativas!?.length === 1}
            loading={cooperativasIsLoading}
            panelFilter
            multiple={cooperativas!?.length > 1 ? true : false}
            preSelectUniqueOption={cooperativas!?.length === 1}
            error={sent && !!validator && validator.coopIsRequired().error}
            helperText={
              sent && !!validator && validator.coopIsRequired().error
                ? validator.coopIsRequired().helperText
                : ''
            }
            clearButton={cooperativas!?.length > 1 ? true : false}
          />
        </Col>
        <Col cols={[12, 6, 6, 2, 2]}>
          <SelectTest
            data-testid={'filtro-por-status'}
            label={homePrefix.filter.statusEvento}
            placeholder={homePrefix.filter.statusEventoPlaceholder}
            options={state.situation}
            value={state.filter.status}
            onChange={(value) => {
              action({
                type: 'setFilter',
                value: {
                  status: value,
                },
              });
            }}
            panelFilter
            multiple
            loading={cooperativasIsLoading}
            disabled={
              (String(state.filter.idEvento) !== '' &&
                state.filter.idEvento !== undefined) ||
              cooperativasIsLoading
            }
            error={sent && !!validator && validator.statusIsRequired().error}
            helperText={
              sent && !!validator && validator.statusIsRequired().error
                ? validator.statusIsRequired().helperText
                : ''
            }
            clearButton={
              String(state.filter.idEvento) === '' ||
              state.filter.idEvento === undefined
            }
          />
        </Col>
        <Col cols={[12, 6, 6, 2, 2]}>
          <Input
            id={'date-from'}
            data-testid={'filtro-por-data-inicio'}
            value={formatData(
              state.filter.startDate.split('-').reverse().join('/')
            )}
            onChange={(evt) => {
              action({
                type: 'setFilter',
                value: {
                  startDate: evt.target.value.split('/').reverse().join('-'),
                },
              });
            }}
            label={homePrefix.filter.dataInicioEvento}
            placeholder={homePrefix.filter.dataInicioEventoPlaceholder}
            rightObject={
              <DatePicker
                onChange={(value) => {
                  action({
                    type: 'setFilter',
                    value: {
                      startDate: value.split('/').reverse().join('-'),
                    },
                  });
                }}
                queryToAppend={'#date-from'}
                initialValue={formatData(
                  state.filter.startDate.split('-').reverse().join('/')
                )}
                closeOnChange
                disabled={
                  (String(state.filter.idEvento) !== '' &&
                    state.filter.idEvento !== undefined) ||
                  cooperativasIsLoading
                }
              />
            }
            error={sent && !!validator && validator.initialDateIsValid().error}
            helperText={
              sent && !!validator && validator.initialDateIsValid().error
                ? validator.initialDateIsValid().helperText
                : ''
            }
            loading={cooperativasIsLoading}
            disabled={
              (String(state.filter.idEvento) !== '' &&
                state.filter.idEvento !== undefined) ||
              cooperativasIsLoading
            }
          />
        </Col>
        <Col cols={[12, 6, 6, 2, 2]}>
          <Input
            id={'date-until'}
            data-testid={'filtro-por-data-fim'}
            value={formatData(
              state.filter.endDate.split('-').reverse().join('/')
            )}
            onChange={(evt) => {
              action({
                type: 'setFilter',
                value: {
                  endDate: evt.target.value.split('/').reverse().join('-'),
                },
              });
            }}
            label={homePrefix.filter.dataFimEvento}
            placeholder={homePrefix.filter.dataFimEventoPlaceholder}
            rightObject={
              <DatePicker
                onChange={(value) => {
                  action({
                    type: 'setFilter',
                    value: {
                      endDate: value.split('/').reverse().join('-'),
                    },
                  });
                }}
                queryToAppend={'#date-until'}
                initialValue={formatData(
                  state.filter.endDate.split('-').reverse().join('/')
                )}
                closeOnChange
                disabled={
                  String(state.filter.idEvento) !== '' &&
                  state.filter.idEvento !== undefined
                }
              />
            }
            error={sent && !!validator && validator.endDateIsValid().error}
            helperText={
              sent && !!validator && validator.endDateIsValid().error
                ? validator.endDateIsValid().helperText
                : ''
            }
            loading={cooperativasIsLoading}
            disabled={
              (String(state.filter.idEvento) !== '' &&
                state.filter.idEvento !== undefined) ||
              cooperativasIsLoading
            }
          />
        </Col>
        <Col cols={[12, 6, 6, 3, 3]}>
          <SelectTest
            id={'is-event-oficial'}
            data-testid={'filtro-por-evento-test-ou-oficial'}
            label={homePrefix.filter.eventoTest}
            placeholder={homePrefix.filter.eventoTestPlaceholder}
            options={testOptions}
            value={state.filter.flgEventoTeste}
            onChange={(value) => {
              console.log(value);

              action({
                type: 'setFilter',
                value: {
                  flgEventoTeste: value,
                },
              });
            }}
            disabled={
              String(state.filter.idEvento) !== '' &&
              state.filter.idEvento !== undefined
            }
            clearButton={
              String(state.filter.idEvento) === '' ||
              state.filter.idEvento === undefined
            }
          />
        </Col>
      </Row>
      <Row>
        <Col
          style={{
            width: '100%',
            display: 'flex',
            gap: '16px',
            justifyContent: 'space-between',
            alignItems: 'center',
          }}
        >
          <div style={{ display: 'flex', gap: '16px' }}>
            {[
              AuthUserPerfilId.PertencerAdmin,
              AuthUserPerfilId.PertencerGestao,
            ].includes(perfil!?.id!) ? (
              <Button
                leftIcon={<AddIcon />}
                theme={ButtonThemes.Primary}
                disabled={
                  ![
                    AuthUserPerfilId.PertencerAdmin,
                    AuthUserPerfilId.PertencerGestao,
                  ].includes(perfil!?.id!)
                }
                onClick={() => {
                  history.push(
                    `${AssessorCadastroEventoRoutePath}etapas/dados`
                  );
                }}
              >
                {homePrefix.filter.novaAssembleia}
              </Button>
            ) : (
              <Fragment />
            )}
            {[
              AuthUserPerfilId.PertencerAdmin,
              AuthUserPerfilId.PertencerGestao,
              AuthUserPerfilId.PertencerConsulta,
            ].includes(perfil!?.id!) ? (
              <Button
                leftIcon={<AddIcon />}
                theme={ButtonThemes.Primary}
                disabled={
                  ![
                    AuthUserPerfilId.PertencerAdmin,
                    AuthUserPerfilId.PertencerGestao,
                    AuthUserPerfilId.PertencerConsulta,
                  ].includes(perfil!?.id!)
                }
                onClick={() => {
                  history.push(
                    `${AssessorCadastroReuniaoRoutePath}etapas/dados`
                  );
                }}
              >
                {homePrefix.filter.novaReunião}
              </Button>
            ) : (
              <Fragment />
            )}
          </div>

          <div
            style={{
              display: 'flex',
              gap: '16px',
            }}
          >
            <Button
              leftIcon={<RefreshIcon animate={false} />}
              theme={ButtonThemes.Secondary}
              disabled={disabledCleanButton}
              onClick={() => {
                sessionStorage.removeItem('filter-assessor');
                action({
                  type: 'setFilter',
                  value: {
                    idEvento: undefined,
                    tiposEventos: eventTypeOptions.map(
                      (x: { value: string }) => x.value
                    ),
                    modalidades: modalidadesOptions.map(
                      (x: { value: string }) => x.value
                    ),
                    status: state.situation.map((x) => x.value),
                    cooperativas:
                      cooperativas!?.length === 1
                        ? cooperativas![0].codCooperativa
                        : cooperativas?.map((coops) => coops.codCooperativa),
                    startDate: initialStartDate,
                    endDate: initialEndDate,
                    flgEventoTeste: null,
                  },
                });
                setTimeout(() => {
                  setDefaultValue(true);
                }, 100);
              }}
            >
              {homePrefix.filter.redefinirFiltro}
            </Button>
            <Button
              leftIcon={<SearchIcon />}
              theme={ButtonThemes.Primary}
              onClick={() => {
                setSent(true);
                if (!validator.allIsValid()) {
                  return;
                }
                onChangeFilter(filters);
                sessionStorage.setItem(
                  'filter-assessor',
                  JSON.stringify(filters)
                );
              }}
              loading={listaEventoIsLoading}
              disabled={
                (sent && !!validator && !validator.allIsValid()) ||
                cooperativasIsLoading ||
                listaEventoIsLoading
              }
            >
              {homePrefix.filter.filtrar}
            </Button>
          </div>
        </Col>
      </Row>
    </Fragment>
  );
}
